import {
    Fragment,
    ReactNode,
    useRef,
    useState,
    createContext,
    useContext
} from "react"

import {
    Dialog, DialogPanel, Transition,
    TransitionChild
} from "@headlessui/react"

import { ExclamationTriangleIcon } from "@heroicons/react/24/outline"
import { Button } from "./Button";

type ConfirmModalProps = {
    open: boolean;
    title: string;
    message?: string[];
    children?: ReactNode;
    cancel?: string;
    confirm?: string;
    hide_cancel?: boolean;
    hide_icon?: boolean;
    size?: "sm" | "md" | "lg" | "xl" | "2xl" | "3xl";
    disable_confirm?: boolean;
    onClose: (result: boolean) => void;
    onLeave?: () => void;
    onEnter?: () => void;
}

const ModalReadyContext = createContext<{ is_modal_ready: boolean }>({ is_modal_ready: true });

export const useModalReady = () => useContext(ModalReadyContext);

export function ConfirmModal(props: ConfirmModalProps) {
    const { open, title, message, children, cancel, confirm, hide_cancel, hide_icon, size: _size, disable_confirm, onClose, onLeave, onEnter } = props;

    const size = _size ?? "lg";
    const [has_entered, setHasEntered] = useState(false);
    const cancelButtonRef = useRef(null);

    return (
        <Transition show={open} as={Fragment}>
            <Dialog as="div" className="relative z-50" initialFocus={cancelButtonRef} onClose={() => onClose(false)}>
                <TransitionChild
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </TransitionChild>

                <div className="fixed inset-0 z-10 overflow-y-auto">
                    <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                        <TransitionChild
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            afterEnter={() => {
                                setHasEntered(true);
                                onEnter?.();
                            }}
                            afterLeave={() => {
                                setHasEntered(false);
                                onLeave?.();
                            }}
                        >
                            <DialogPanel className={`relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-${size} sm:p-6`}>
                                <div className="sm:flex sm:items-start">
                                    {!hide_icon && <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-torch_red-100 sm:mx-0 sm:h-10 sm:w-10">
                                        <ExclamationTriangleIcon className="h-6 w-6 text-torch_red-600" aria-hidden="true" />
                                    </div>}
                                    <div className="text-center sm:ml-4 sm:mt-0 sm:text-left w-full">
                                        <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
                                            {title}
                                        </Dialog.Title>
                                        <div className="mt-2">
                                            {message && message.map((m, i) => <p key={i} className="text-sm mt-2 text-gray-500">{m}</p>)}
                                            <ModalReadyContext.Provider value={{ is_modal_ready: has_entered }}>
                                                {children}
                                            </ModalReadyContext.Provider>
                                        </div>
                                    </div>
                                </div>
                                <div className="mt-5 sm:flex justify-center">
                                    <Button text={confirm ?? "OK"} onClick={() => onClose(true)} highlight={true} disabled={disable_confirm} />
                                    {!hide_cancel && <Button text={cancel ?? "Cancel"} onClick={() => onClose(false)} />}
                                </div>
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </div>
            </Dialog>
        </Transition>
    )
}
